import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';

import { cn } from '@utils/cn';

import { extractValidChars, isValidChar } from './utils';

import type { InputOTPProps, InputSlotProps } from './InputOTP.types';

import { inputSlotClasses, wrapperClasses } from './InputOTP.styles';

const InputSlot = forwardRef<HTMLInputElement, InputSlotProps>(
  (
    { className, value, onChange, onKeyUp, onPaste, validationStatus, inputMode, ...props },
    ref
  ) => (
    <input
      ref={ref}
      className={cn(inputSlotClasses({ validationStatus }), className)}
      name="otp"
      autoComplete="one-time-code"
      value={value}
      onChange={onChange}
      onKeyUp={onKeyUp}
      onPaste={onPaste}
      inputMode={inputMode}
      aria-label="OTP Digit/Character"
      {...props}
    />
  )
);

export const InputOTP: React.FC<InputOTPProps> = ({
  className,
  length,
  value = '',
  onValueChange,
  isValid,
  inputMode = 'numeric'
}) => {
  const [otpValues, setOtpValues] = useState<string[]>(() => {
    const chars = value ? value.slice(0, length).split('') : [];
    return Array.from({ length }, (_, i) => chars[i] || '');
  });

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const isComplete = otpValues.every(Boolean);
  const pasteTimeoutRef = useRef(null);
  const isPasteOperationRef = useRef(false);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    const chars = value ? value.slice(0, length).split('') : [];
    setOtpValues(Array.from({ length }, (_, i) => chars[i] || ''));
  }, [value, length]);
  /* eslint-enable react-hooks/set-state-in-effect */

  useEffect(() => {
    if (isComplete) {
      onValueChange(otpValues.join(''));
    }
  }, [isComplete, otpValues, onValueChange]);

  const focusNextInput = useCallback(
    (currentIndex: number) => {
      const nextIndex = currentIndex + 1;
      if (nextIndex < length) {
        const nextInput = inputRefs.current[nextIndex];
        if (nextInput) {
          nextInput.focus();
        }
      }
    },
    [length]
  );

  const clearField = useCallback((index: number) => {
    setOtpValues((prevOtpValues) => {
      const updatedOtpValues = [...prevOtpValues];
      updatedOtpValues[index] = '';
      return updatedOtpValues;
    });
  }, []);

  const updateField = useCallback((index: number, char: string) => {
    setOtpValues((prevOtpValues) => {
      const updatedOtpValues = [...prevOtpValues];
      updatedOtpValues[index] = char;
      return updatedOtpValues;
    });
  }, []);

  /* Handles input change for each OTP field:
   - Takes the first character of the input.
   - Checks if it's valid for the current inputMode.
   - If valid, updates the OTP value at the given index.
   */
  const handleInputChange = useCallback(
    (index: number, inputValue: string) => {
      const firstChar = inputValue.slice(0, 1).toUpperCase();
      if (!isValidChar(firstChar, inputMode)) {
        return;
      }

      updateField(index, firstChar);
    },
    [inputMode, updateField]
  );

  const handleKeyUp = useCallback(
    (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
      // Ignore keyups that are part of a keyboard shortcut (e.g., Cmd/Ctrl+V) or that occur immediately after a paste operation
      if (event.ctrlKey || event.metaKey || event.altKey || isPasteOperationRef.current) {
        return;
      }

      const { key } = event;

      if (key === 'Backspace') {
        event.preventDefault();
        clearField(index);
        // focusPreviousInput(index);
        return;
      }

      // Ignore non-character keys (ArrowLeft, Tab, etc.)
      if (key.length !== 1) {
        return;
      }

      const char = key.toUpperCase();
      if (!isValidChar(char, inputMode)) {
        return;
      }

      updateField(index, char);
      focusNextInput(index);
    },
    [focusNextInput, inputMode, clearField, updateField]
  );

  const handlePaste = useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>) => {
      event.preventDefault();

      // Set flag to prevent key events from interfering with paste
      isPasteOperationRef.current = true;

      // Clear any existing timeout
      if (pasteTimeoutRef.current) {
        clearTimeout(pasteTimeoutRef.current);
      }

      // Reset the flag after a short delay to allow paste to complete
      pasteTimeoutRef.current = setTimeout(() => {
        isPasteOperationRef.current = false;
      }, 100);

      const pasted = event.clipboardData.getData('text');
      const validChars: string[] = extractValidChars(pasted, inputMode);

      if (!pasted || !validChars.length) {
        return;
      }

      const focusedIndex = inputRefs.current.findIndex((ref) => ref === document.activeElement);
      const startIndex = focusedIndex !== -1 ? focusedIndex : 0;

      setOtpValues((prevOtpValues) => {
        const updatedOtpValues = [...prevOtpValues];
        validChars.forEach((char, i) => {
          const index = startIndex + i;
          if (index < length && isValidChar(char, inputMode)) {
            updatedOtpValues[index] = char.toUpperCase();
          }
        });
        return updatedOtpValues;
      });

      const nextIndex = Math.min(startIndex + validChars.length, length - 1);
      if (nextIndex < length) {
        const nextInput = inputRefs.current[nextIndex];
        if (nextInput) {
          nextInput.focus();
        }
      }
    },
    [inputMode, length]
  );

  const setInputRef = (index: number) => (element: HTMLInputElement | null) => {
    inputRefs.current[index] = element;
  };

  const validationStatus = isComplete ? isValid : undefined;

  return (
    <div className={cn(wrapperClasses, className)}>
      {Array.from({ length }, (_, index) => (
        <InputSlot
          key={index}
          ref={setInputRef(index)}
          value={otpValues[index]}
          onChange={(e) => handleInputChange(index, e.currentTarget.value)}
          onKeyUp={(e) => handleKeyUp(index, e)}
          onPaste={handlePaste}
          validationStatus={validationStatus}
          inputMode={inputMode}
        />
      ))}
    </div>
  );
};
